import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { db, formatDate, invoiceDownloadHref, invoiceTotalCents, money, type InvoiceRecord } from "@/lib/billing";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";

async function fetchInvoices(userId: string, companyId: string | null): Promise<InvoiceRecord[]> {
  const query = db
    .from("invoices")
    .select("*, packages(name)")
    .order("created_at", { ascending: false });
  const { data, error } = await (companyId
    ? query.or(`company_id.eq.${companyId},user_id.eq.${userId}`)
    : query.eq("user_id", userId));
  if (error) throw error;
  return data ?? [];
}

export default function BillingInvoicesPage() {
  const { user } = useAuth();
  const { currentCompanyId, role, isPlatformOwner } = useCompany();
  const canViewBilling = isPlatformOwner || role === "owner" || role === "admin";
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["my-invoices", user?.id, currentCompanyId],
    queryFn: () => fetchInvoices(user!.id, currentCompanyId),
    enabled: !!user && canViewBilling,
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Invoices</h1>
          <p className="text-xs text-muted-foreground">Stripe-generated Delapresse invoices for your account.</p>
        </div>
      </header>

      <main className="p-6">
        {!canViewBilling ? (
          <Card>
            <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
              Billing invoices are available only to company owners and admins.
            </CardContent>
          </Card>
        ) : (
          <Card>
          <CardHeader><CardTitle className="text-base">Your invoices</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : invoices.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No invoices yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Links</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const generatedPdfHref = invoiceDownloadHref(invoice);
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.invoice_number ?? "Pending"}</TableCell>
                        <TableCell>{invoice.packages?.name ?? "Package unavailable"}</TableCell>
                        <TableCell>{money(invoiceTotalCents(invoice), invoice.currency, true)}</TableCell>
                        <TableCell><StatusBadge status={invoice.status} /></TableCell>
                        <TableCell>{formatDate(invoice.due_date)}</TableCell>
                        <TableCell className="space-x-2 text-right">
                          {invoice.hosted_invoice_url && <Button asChild variant="outline" size="sm"><a href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>}
                          {generatedPdfHref && (
                            <Button asChild variant="outline" size="sm">
                              <a href={generatedPdfHref} download={invoice.generated_invoice_pdf_filename ?? undefined} target={invoice.generated_invoice_pdf_base64 ? undefined : "_blank"} rel={invoice.generated_invoice_pdf_base64 ? undefined : "noreferrer"}><FileDown className="h-4 w-4" /></a>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
